/**
 * O ecrã de bloqueio por inactividade: tapa o painel inteiro (o conteúdo fica desfocado por
 * trás) até a pessoa voltar a pôr a palavra-passe (`/tela-bloqueio/desbloquear`). O
 * `MainProvider` bloqueia-o e sincroniza o estado entre separadores; aqui só se desbloqueia.
 *
 * O `FormTelaBloqueio` também serve a página `tela_bloqueio`.
 */
import * as React from "react";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { usePage } from "@inertiajs/react";
import { LockKeyhole, LockOpen, LogOut } from "lucide-react";
import { Avatar, Botao, EntradaSenha } from "@/Logigate/ui";
import { CamadaExclusiva } from "@/Logigate/components/CamadaExclusiva";
import { CustomRequest } from "@/utils/CustomRequest";
import { showPop } from "@/utils/Pops";

interface Props {
    open: boolean;
    onClose?: () => void;
    onOk?: () => void;
}

export default function ModalLockScreen(props: Props) {
    const { props: pagina } = usePage<any>();
    const idTitulo = useId();
    const empresa: string | undefined = pagina.empresa?.nome;

    return (
        <CamadaExclusiva aberta={props.open} idTitulo={idTitulo}>
            <div className="w-full max-w-sm animate-in fade-in-0 zoom-in-95">
                <div className="rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-zinc-950/5 sm:p-8 dark:bg-zinc-900 dark:ring-white/10">
                    <div className="mb-6 flex justify-center">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-600 dark:bg-white/5 dark:text-zinc-300">
                            <LockKeyhole className="size-3.5" aria-hidden />
                            <span id={idTitulo}>Painel bloqueado</span>
                        </span>
                    </div>
                    <FormTelaBloqueio onOk={() => props.onOk?.()} />
                    <p className="mt-4 text-center text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                        Bloqueámos o painel{empresa ? ` de ${empresa}` : ""} por inactividade. Introduz a tua palavra-passe para continuar.
                    </p>
                </div>
                <TerminarSessaoDoBloqueio className="mt-3 text-white/80 hover:bg-white/10 hover:text-white" />
            </div>
        </CamadaExclusiva>
    );
}

/**
 * «Não és tu? Terminar sessão»: quem encontra o painel de outra pessoa bloqueado pode sair sem a
 * palavra-passe dela — na janela do bloqueio e no ecrã bloqueado (a página que abre ao recarregar).
 * Como o «Sair» do casco: o `/sair` da conta só termina a sessão com `t` (o csrf), não com `token`.
 */
export function TerminarSessaoDoBloqueio({ className }: { className?: string }) {
    const urls = (window as any).urls ?? {};
    if (!urls.conta) {
        return null;
    }
    const sair = `${urls.conta}/sair?t=${encodeURIComponent((window as any).token ?? "")}`;
    return (
        <a href={sair} className={`mx-auto flex min-h-11 w-fit items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors ${className ?? ""}`}>
            <LogOut className="size-4" aria-hidden />
            Não és tu? Terminar sessão
        </a>
    );
}

/** A foto, o nome e a palavra-passe para desbloquear. `onOk` recebe o `payload` da resposta. */
export const FormTelaBloqueio = (props: { onOk?: (payload?: any) => void }) => {
    const { props: pagina } = usePage<any>();
    const [aEnviar, setAEnviar] = useState(false);
    const utilizador = pagina.auth?.user;

    const {
        register,
        setError,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            password: "",
        },
    });

    const onSubmit = async (params: { password: string }) => {
        setAEnviar(true);
        const res: any = await CustomRequest(pagina.base_path + "/tela-bloqueio/desbloquear", params, "POST");
        setAEnviar(false);
        if (res.estado === "ok") {
            props.onOk?.(res.payload);
        } else if (res.input) {
            setError(res.input as "password", { message: res.texto });
        } else {
            showPop("", res.texto, "error");
        }
    };

    return (
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="w-full space-y-5">
            <div className="flex flex-col items-center text-center">
                <Avatar src={utilizador?.foto_link} nome={utilizador?.nome} tamanho="xl" className="shadow-md ring-4 ring-white dark:ring-zinc-900" />
                {utilizador?.nome && <p className="mt-3 text-lg font-semibold text-zinc-900 dark:text-zinc-50">{utilizador.nome}</p>}
                {utilizador?.email && <p className="text-[13px] text-zinc-500 dark:text-zinc-400">{utilizador.email}</p>}
            </div>

            <EntradaSenha
                {...register("password", { required: "Preenche a palavra-passe" })}
                rotulo="Palavra-passe"
                erro={errors.password?.message}
                autoFocus
                tamanho="lg"
            />

            <Botao type="submit" larguraTotal tamanho="lg" aCarregar={aEnviar} icone={<LockOpen className="size-4" />}>
                Desbloquear
            </Botao>
        </form>
    );
};
